/**
 * Lagen in de URL (Vandaag v2, fase 5; docs/heute-vandaag-v2-plan.md). Een
 * tray staat in de zoekparameters (?tray=, ?prik=, ?split=, ?plan=), de
 * plan-lade in het pad (/vandaag/plan/:id). Twee dingen regelt dit bestand:
 *
 * - De geschiedenis. Openen is een nieuwe stap (push, met state.laag: het
 *   aantal lagen boven de pagina eronder), een andere tray kiezen vervangt
 *   die stap, en sluiten gaat terug (navigate(-1)) als de laag zelf een stap
 *   was. Zo brengt Terug in de browser een gesloten tray nooit terug. Een
 *   laag zonder eigen stap (een diepe link, een push-melding) sluit door de
 *   URL te vervangen.
 * - Het verversen. Een laag openen of sluiten is geen reden om de loaders
 *   opnieuw te draaien: wat eronder staat, blijft hetzelfde. Een actie
 *   (formulier, fetcher.submit) ververst wél altijd, en revalidate() op
 *   dezelfde URL ook (de tray die een link maakt, wil hem daarna zien).
 *
 * Geen DOM bij het importeren; de vandaag-route en de plan-route halen
 * vandaagHervalideren en useLaagParams hier vandaan.
 */
import { useCallback, useMemo, useRef } from 'react'
import {
	type Location,
	type ShouldRevalidateFunctionArgs,
	useLocation,
	useNavigate,
	useNavigation,
} from 'react-router'

/** De zoekparameters die alleen een laag openen (geen gegevens kiezen).
 *  ?groot=1 zet de plan-lade over het hele scherm (PlanLade): ook een laag,
 *  met een eigen stap, zodat Terug je in dezelfde lade terugbrengt. */
export const LAAG_PARAMS = ['tray', 'plan', 'prik', 'split', 'groot'] as const

const LAAG_SET: ReadonlySet<string> = new Set(LAAG_PARAMS)

const BASIS = 'http://heute.invalid'

/** /vandaag/plan/:id: de plan-lade boven Vandaag. */
const LADE_PAD = /^\/vandaag\/plan\/([^/]+)\/?$/

export function isLadePad(pathname: string): boolean {
	return LADE_PAD.test(pathname)
}

/** Het plan-id uit een lade-pad, of null als het pad geen lade is. */
export function ladePlanId(pathname: string): string | null {
	const id = LADE_PAD.exec(pathname)?.[1]
	return id ? decodeURIComponent(id) : null
}

/**
 * Wat er in history.state meereist met een laag. `laag` telt de lagen boven
 * de pagina eronder (de lade is er één, een tray erin nog één); `van` is het
 * adres van de stap ervoor (pad plus zoekdeel), dus waar navigate(-1) op
 * uitkomt. sluit() gaat alleen terug als dat ook het adres zonder de
 * gesloten laag is; anders vervangt het de URL. Een link die de lade en een
 * tray in één stap opent (/vandaag/plan/:id?prik=…) geeft als `van` het
 * Vandaag-adres mee waar hij vandaan kwam: dan sluit de tray naar de lade,
 * en sluit de lade daarna terug naar Vandaag. Zonder `van` gaat sluiten
 * altijd terug.
 */
export type LaagState = { laag: number; van?: string }

/** Hoeveel lagen er boven de pagina eronder liggen volgens deze stap. */
export function laagDiepte(state: unknown): number {
	if (!state || typeof state !== 'object') return 0
	const laag = (state as { laag?: unknown }).laag
	return typeof laag === 'number' && laag > 0 ? Math.floor(laag) : 0
}

function laagVan(state: unknown): string | undefined {
	if (!state || typeof state !== 'object') return undefined
	const van = (state as { van?: unknown }).van
	return typeof van === 'string' ? van : undefined
}

/**
 * Ligt deze stap (een tray) op een lade die geen eigen stap had? Een koude
 * treffer of een vervangen adres: de tray telt 1 laag en kwam van een
 * lade-pad, dus één stap terug komt uit op die lade, niet op Vandaag. Wie
 * vanaf zo'n stap de lade sluit of door een ander plan vervangt, neemt ook
 * de stap van die lade over (useLadeDicht, de replan-tray): anders brengt
 * Terug de gesloten lade terug.
 */
export function opLadeZonderStap(state: unknown): boolean {
	const van = laagVan(state)
	return (
		laagDiepte(state) === 1 &&
		van !== undefined &&
		isLadePad(new URL(van, BASIS).pathname)
	)
}

/** Vandaag zelf, met of zonder schuine streep. */
const VANDAAG_PAD = /^\/vandaag\/?$/

/**
 * Het plan in de lade die Vandaag toont, uit het pad dat getoond wordt en
 * de navigatie die loopt (useNavigation()). Een gewone navigatie telt
 * zodra hij begint: naar een lade staat de voorproef er meteen, en terug
 * naar Vandaag is de lade meteen weg, niet pas als de loader van Vandaag
 * klaar is. Zo blijft er nooit een uitgespeelde lade hangen die een nieuwe
 * tik op hetzelfde plan opslokt. Een formulier en een navigatie naar een
 * andere pagina laten de lade staan tot ze klaar zijn.
 */
export function ladeIdVoor(
	getoond: string,
	navigatie: {
		state: string
		formMethod?: string
		location?: { pathname: string }
	},
): string | null {
	const nu = ladePlanId(getoond)
	const onderweg =
		navigatie.state === 'loading' && !navigatie.formMethod
			? navigatie.location
			: undefined
	if (!onderweg) return nu
	return (
		ladePlanId(onderweg.pathname) ??
		(VANDAAG_PAD.test(onderweg.pathname) ? null : nu)
	)
}

function waarden(params: URLSearchParams, key: string) {
	return params.getAll(key).join('\u0000')
}

/**
 * Verschillen twee URL's alleen in laag-parameters? Zelfde pad, minstens één
 * sleutel anders, en elke sleutel die anders is staat in LAAG_PARAMS. Twee
 * gelijke URL's (revalidate()) tellen niet: die moeten blijven verversen.
 */
export function alleenLaagParams(a: URL, b: URL): boolean {
	if (a.pathname !== b.pathname) return false
	const sleutels = new Set([...a.searchParams.keys(), ...b.searchParams.keys()])
	let anders = false
	for (const key of sleutels) {
		if (waarden(a.searchParams, key) === waarden(b.searchParams, key)) continue
		if (!LAAG_SET.has(key)) return false
		anders = true
	}
	return anders
}

/**
 * shouldRevalidate voor een route waarvan de loader niets met de lagen doet
 * (de planpagina en de plan-lade): alleen een laag erbij of eraf, ook met
 * Terug in de browser (een POP kan geen defaultShouldRevalidate meegeven),
 * laat de loader staan. Een actie, revalidate() op dezelfde URL en elke
 * andere wissel volgen React Router.
 */
export function laagHervalideren({
	currentUrl,
	nextUrl,
	formMethod,
	defaultShouldRevalidate,
}: Pick<
	ShouldRevalidateFunctionArgs,
	'currentUrl' | 'nextUrl' | 'formMethod' | 'defaultShouldRevalidate'
>): boolean {
	if (!formMethod && alleenLaagParams(currentUrl, nextUrl)) return false
	return defaultShouldRevalidate
}

/**
 * shouldRevalidate voor Vandaag. In deze volgorde:
 * 1. Een actie ververst altijd (een blokkade haalt de kaart weg).
 * 2. De lade gaat open: Vandaag staat stil erachter.
 * 3. Van lade naar lade (een ander plan, een tray erin, revalidate() vanuit
 *    het plan): Vandaag blijft staan.
 * 4. De lade gaat dicht: één keer bijwerken wat er intussen veranderde.
 * 5. Alleen een laag op Vandaag zelf (?tray=, ?plan=): niets te halen.
 * 6. Anders wat React Router zelf zou doen.
 */
export function vandaagHervalideren({
	currentUrl,
	nextUrl,
	formMethod,
	defaultShouldRevalidate,
}: ShouldRevalidateFunctionArgs): boolean {
	if (formMethod) return defaultShouldRevalidate
	const nuLade = isLadePad(currentUrl.pathname)
	const straksLade = isLadePad(nextUrl.pathname)
	if (!nuLade && straksLade) return false
	if (nuLade && straksLade) return false
	if (nuLade && !straksLade) return true
	if (alleenLaagParams(currentUrl, nextUrl)) return false
	return defaultShouldRevalidate
}


function alsUrl(pathname: string, search: string) {
	return new URL(`${pathname}${search}`, BASIS)
}

function zoekdeel(params: URLSearchParams) {
	const s = params.toString()
	return s ? `?${s}` : ''
}

/** Zelfde pad en dezelfde parameters, ongeacht hun volgorde. */
export function zelfdeAdres(a: string, b: string): boolean {
	const ua = new URL(a, BASIS)
	const ub = new URL(b, BASIS)
	if (ua.pathname !== ub.pathname) return false
	ua.searchParams.sort()
	ub.searchParams.sort()
	return ua.searchParams.toString() === ub.searchParams.toString()
}

/** Opent dit adres een laag (staat er een laag-parameter in)? */
export function heeftLaag(adres: string): boolean {
	const params = new URL(adres, BASIS).searchParams
	return LAAG_PARAMS.some((key) => params.has(key))
}

/** history.state van React Router's browsergeschiedenis, of null (op de
 *  server, of in een geheugen-router zoals in tests: die zet geen idx). */
function routerHistoryState(): { key?: unknown } | null {
	if (typeof window === 'undefined') return null
	const state: unknown = window.history.state
	if (!state || typeof state !== 'object' || !('idx' in state)) return null
	return state as { key?: unknown }
}

/**
 * De stap waar de browser nu echt op staat. navigate(-n) telt vanaf die
 * stap, maar wat React Router toont (useLocation) loopt na Terug in de
 * browser achter tot de loaders klaar zijn: de browser staat dan al een
 * stap terug terwijl het scherm nog de laag toont. Wie in die tijd sluit
 * met navigate(-1), gaat twee stappen terug en kan zo de app uit.
 *
 * React Router zet in history.state de sleutel van elke stap (key; de
 * eerste stap na het laden heeft er geen en heet dan 'default'). Geeft:
 * - `getoond` als de browser daar staat (ook zonder browsergeschiedenis);
 * - `onderweg` (useNavigation().location) als de browser daar al staat:
 *   een Terug die nog laadt;
 * - null als de browser op een stap staat die geen van beide is.
 */
export function browserStap(
	getoond: Location,
	onderweg?: Location | null,
): Location | null {
	const state = routerHistoryState()
	if (!state) return getoond
	const sleutel =
		typeof state.key === 'string' && state.key ? state.key : 'default'
	if (sleutel === getoond.key) return getoond
	if (onderweg && onderweg.key === sleutel) return onderweg
	return null
}

/** Het adres (pad en zoekdeel) waar de browser nu staat, of null zonder
 *  browsergeschiedenis van React Router (op de server, in tests). */
export function browserAdres(): string | null {
	if (!routerHistoryState()) return null
	return `${window.location.pathname}${window.location.search}`
}

/**
 * Kan de browser `n` stappen terug binnen de app? React Router nummert de
 * stappen in history.state (idx; 0 is de stap waarop de app begon). Telt
 * een laag meer stappen dan er vóór liggen (een telfout), dan gaat
 * navigate(-n) de app uit, of doet niets in een app die op deze stap
 * begon; dan vervangt sluiten de URL. Zonder browsergeschiedenis (op de
 * server, in tests): ja.
 */
export function kanTerug(n: number): boolean {
	const idx = (routerHistoryState() as { idx?: unknown } | null)?.idx
	return typeof idx !== 'number' || idx >= n
}

/**
 * Lagen openen en sluiten via de zoekparameters, met de geschiedenis zoals
 * bovenaan beschreven. open() zet parameters (null haalt er een weg);
 * met `wissel` vervangt het de huidige stap (een andere tray in plaats van
 * deze). sluit() haalt de genoemde parameters weg; staat er geen, dan doet
 * het niets. Alles met preventScrollReset: wat eronder ligt, scrolt niet.
 *
 * De functies lezen de locatie van het moment van aanroepen (niet van de
 * render waarin ze gemaakt zijn): een tray roept onClose pas na zijn
 * uitgang aan. Allebei rekenen ze vanaf de stap waar de browser echt staat
 * (browserStap). Laadt er nog een Terug die de laag al weghaalt, dan doet
 * sluit() niets, in plaats van nog een stap terug te gaan, en opent open()
 * de nieuwe laag op die stap (een tik op de status-chip terwijl Vandaag na
 * de lade nog ververst, opent de status-tray en niet de lade opnieuw). Ligt
 * er vóór deze stap geen stap van de app (kanTerug), dan vervangt sluit()
 * de URL.
 */
export function useLaagParams(): {
	open(set: Record<string, string | null>, o?: { wissel?: boolean }): void
	sluit(keys: ReadonlyArray<string>): void
} {
	const location = useLocation()
	const navigate = useNavigate()
	const navigation = useNavigation()
	const locatieRef = useRef<Location>(location)
	locatieRef.current = location
	const onderwegRef = useRef<Location | undefined>(navigation.location)
	onderwegRef.current = navigation.location

	const open = useCallback(
		(set: Record<string, string | null>, o?: { wissel?: boolean }) => {
			// vanaf de stap waar de browser echt staat: laadt er nog een
			// Terug (de lade sluit, Vandaag ververst), dan toont React Router
			// nog het oude adres, en zou de laag daarop openen
			const nu =
				browserStap(locatieRef.current, onderwegRef.current) ??
				locatieRef.current
			const params = new URLSearchParams(nu.search)
			for (const [key, value] of Object.entries(set)) {
				if (value === null) params.delete(key)
				else params.set(key, value)
			}
			const search = zoekdeel(params)
			if (search === nu.search) return
			const alleenLaag = alleenLaagParams(
				alsUrl(nu.pathname, nu.search),
				alsUrl(nu.pathname, search),
			)
			const opties = {
				preventScrollReset: true,
				...(alleenLaag ? { defaultShouldRevalidate: false } : {}),
			}
			const naar = { pathname: nu.pathname, search }
			if (o?.wissel) {
				void navigate(naar, { ...opties, replace: true, state: nu.state })
			} else {
				const state: LaagState = {
					laag: laagDiepte(nu.state) + 1,
					van: `${nu.pathname}${nu.search}`,
				}
				void navigate(naar, { ...opties, state })
			}
		},
		[navigate],
	)

	const sluit = useCallback(
		(keys: ReadonlyArray<string>) => {
			// de browser staat op een stap die we niet kennen: niet gokken
			const nu = browserStap(locatieRef.current, onderwegRef.current)
			if (!nu) return
			const params = new URLSearchParams(nu.search)
			if (!keys.some((key) => params.has(key))) return
			for (const key of keys) params.delete(key)
			const search = zoekdeel(params)
			const doel = `${nu.pathname}${search}`
			const van = laagVan(nu.state)
			// terug als deze stap de laag zelf was; een link die twee lagen in
			// één stap opende, zegt in `van` waar de bovenste op uitkomt
			if (
				laagDiepte(nu.state) > 0 &&
				(van === undefined || zelfdeAdres(van, doel)) &&
				kanTerug(1)
			) {
				void navigate(-1)
				return
			}
			const alleenLaag = alleenLaagParams(
				alsUrl(nu.pathname, nu.search),
				alsUrl(nu.pathname, search),
			)
			void navigate(
				{ pathname: nu.pathname, search },
				{
					replace: true,
					preventScrollReset: true,
					// de stap houdt zijn diepte: de lade eronder sluit nog steeds
					// met navigate(-laag) naar waar hij vandaan kwam
					state: nu.state,
					...(alleenLaag ? { defaultShouldRevalidate: false } : {}),
				},
			)
		},
		[navigate],
	)

	return useMemo(() => ({ open, sluit }), [open, sluit])
}
